import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { rewriteChapter } from "@/lib/rewriteChapter";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, CopyCheck, ScanSearch, Coins, Wand2, Merge, Check, RotateCcw, ShieldCheck } from "lucide-react";

const SCAN_COST = 2;
const REWRITE_COST = 4;
const SEV = {
  high: { label: "Near-identical", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  medium: { label: "Heavy overlap", cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  low: { label: "Partial overlap", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
};

export default function DedupeDialog({ open, onOpenChange, projectId, onJump, onChanged }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);            // `${groupId}:${action}` while working
  const [keepPick, setKeepPick] = useState({});      // groupId -> chapterId to keep
  const [done, setDone] = useState({});              // chapterId -> "rewritten" ; groupId -> "merged"
  const [confirm, setConfirm] = useState(null);      // {group, keepId, removeIds}
  const [deleted, setDeleted] = useState([]);

  const titleOf = useCallback(
    (id) => (report?.chapters || []).find((c) => c.id === id)?.title || "this chapter",
    [report],
  );

  const loadDeleted = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/deleted-chapters`);
      setDeleted(data || []);
    } catch { /* ignore */ }
  }, [projectId]);

  const loadReport = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/duplicates/report`);
      if (data?.groups?.length) setReport(data);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => {
    if (open) { setReport(null); setDone({}); setKeepPick({}); setConfirm(null); loadReport(); loadDeleted(); }
  }, [open, loadReport, loadDeleted]);

  const runScan = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/duplicates/scan`);
      setReport(data); setDone({}); setKeepPick({});
      toast.success(data.groups?.length
        ? `Found ${data.groups.length} set(s) of repeated chapters`
        : "No repeated chapters found — your story moves forward cleanly!");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Repeat scan failed — please try again");
    } finally { setLoading(false); }
  };

  const keepId = (g) => keepPick[g.id] || g.chapter_ids[0];

  const rewriteOne = async (g, chapterId) => {
    const kTitle = titleOf(keepId(g));
    const reason = `This chapter currently repeats the same part of the story as "${kTitle}"`
      + (g.overlap ? ` (both cover: ${g.overlap})` : "")
      + `. Rewrite it so it does NOT re-tell those events. Instead, move the story forward from where "${kTitle}" ends, covering new ground that belongs to this chapter's place in the book.`;
    setBusy(`${g.id}:rw:${chapterId}`);
    try {
      await rewriteChapter(projectId, chapterId, { reason, target: "short" });
      setDone((d) => ({ ...d, [chapterId]: "rewritten" }));
      onChanged?.();
      toast.success(`"${titleOf(chapterId)}" rewritten to move forward. The old text is saved in that chapter's History.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Rewrite failed — please try again"));
    } finally { setBusy(null); }
  };

  const doMerge = async (g, kId, removeIds) => {
    setBusy(`${g.id}:merge`);
    try {
      await api.post(`/projects/${projectId}/chapters/merge`, { keep_id: kId, remove_ids: removeIds });
      setDone((d) => ({ ...d, [g.id]: "merged" }));
      onChanged?.();
      await loadDeleted();
      toast.success(`Kept "${titleOf(kId)}", removed ${removeIds.length} duplicate(s). They're recoverable below.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't merge — please try again");
    } finally { setBusy(null); setConfirm(null); }
  };

  const restore = async (b) => {
    setBusy(`restore:${b.id}`);
    try {
      await api.post(`/projects/${projectId}/deleted-chapters/${b.id}/restore`);
      onChanged?.();
      await loadDeleted();
      toast.success(`"${b.title}" restored to the end of your book.`);
    } catch {
      toast.error("Couldn't restore — please try again");
    } finally { setBusy(null); }
  };

  const groups = report?.groups || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!loading) onOpenChange(v); }}>
      <DialogContent className="bg-popover max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="dedupe-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <CopyCheck className="w-5 h-5 text-primary" /> Find &amp; fix repeated chapters
          </DialogTitle>
        </DialogHeader>

        {!report ? (
          <div className="py-8 text-center space-y-4">
            <ScanSearch className="w-10 h-10 mx-auto text-primary" />
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Scans your book for chapters that tell the <b>same part of the story</b>. It only reports what it finds —
              nothing is changed until you choose to. You can rewrite a repeat so it moves the story forward (your
              original text is kept in that chapter's History), or merge true duplicates into one (removed chapters stay
              recoverable). Your story is never silently lost.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Coins className="w-3.5 h-3.5" /> {SCAN_COST} credits per scan
            </div>
            <Button onClick={runScan} disabled={loading} data-testid="dedupe-run" className="gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />}
              {loading ? "Scanning for repeats…" : "Scan for repeated chapters"}
            </Button>
            {loading && <p className="text-[11px] text-muted-foreground">This can take up to a minute for a full book.</p>}
            {deleted.length > 0 && <RestorePanel deleted={deleted} busy={busy} onRestore={restore} />}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {groups.length ? `${groups.length} set(s) of repeated chapters` : "No repeated chapters found — nicely done!"}
              </p>
              <Button size="sm" variant="outline" onClick={runScan} disabled={loading} data-testid="dedupe-rerun" className="gap-1.5">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanSearch className="w-3.5 h-3.5" />} Re-scan ({SCAN_COST})
              </Button>
            </div>

            <div className="overflow-y-auto -mx-1 px-1 space-y-3 flex-1" data-testid="dedupe-groups">
              {groups.map((g) => {
                const sev = SEV[g.severity] || SEV.medium;
                const kId = keepId(g);
                const merged = done[g.id] === "merged";
                return (
                  <div key={g.id} data-testid="dedupe-group"
                    className={`rounded-lg border border-border bg-secondary/30 p-3 space-y-2.5 ${merged ? "opacity-60" : ""}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`text-[10px] ${sev.cls}`}>{sev.label}</Badge>
                      <span className="font-medium text-sm">{g.chapter_ids.length} chapters tell the same part</span>
                      {merged && <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30 gap-1"><Check className="w-3 h-3" /> Merged</Badge>}
                    </div>
                    {g.overlap && <p className="text-xs text-muted-foreground">Overlap: {g.overlap}</p>}

                    {!merged && (
                      <>
                        <div className="text-[11px] text-muted-foreground">Which one should stay as the canonical version?</div>
                        <div className="space-y-1.5">
                          {g.chapter_ids.map((cid) => {
                            const isKeep = cid === kId;
                            const wasRewritten = done[cid] === "rewritten";
                            return (
                              <div key={cid} data-testid={`dedupe-chapter-${cid}`}
                                className="flex items-center gap-2 rounded-md border border-border bg-background/50 px-2 py-1.5">
                                <button type="button" onClick={() => setKeepPick((p) => ({ ...p, [g.id]: cid }))}
                                  data-testid={`dedupe-keep-${cid}`}
                                  className={`w-4 h-4 rounded-full border shrink-0 grid place-items-center transition-colors ${isKeep ? "border-primary bg-primary" : "border-muted-foreground/50"}`}>
                                  {isKeep && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                                </button>
                                <button type="button" onClick={() => onJump?.(cid)}
                                  className="flex-1 min-w-0 text-left text-xs truncate hover:text-primary transition-colors">
                                  {titleOf(cid)}
                                </button>
                                {isKeep ? (
                                  <span className="text-[10px] text-primary font-semibold shrink-0">Keep</span>
                                ) : wasRewritten ? (
                                  <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30 gap-1 shrink-0"><Check className="w-3 h-3" /> Rewritten</Badge>
                                ) : (
                                  <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs shrink-0"
                                    data-testid={`dedupe-rewrite-${cid}`} disabled={!!busy}
                                    onClick={() => rewriteOne(g, cid)}>
                                    {busy === `${g.id}:rw:${cid}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                                    Rewrite forward ({REWRITE_COST})
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/60">
                          <p className="text-[11px] text-muted-foreground flex-1">
                            Truly the same chapter? Merge into "{titleOf(kId)}" and remove the {g.chapter_ids.length - 1} extra(s) — recoverable below.
                          </p>
                          <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs shrink-0 border-red-500/40 text-red-400 hover:bg-red-500/10"
                            data-testid={`dedupe-merge-${g.id}`} disabled={!!busy}
                            onClick={() => setConfirm({ group: g, keepId: kId, removeIds: g.chapter_ids.filter((c) => c !== kId) })}>
                            {busy === `${g.id}:merge` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Merge className="w-3.5 h-3.5" />}
                            Merge &amp; remove extras
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {deleted.length > 0 && <RestorePanel deleted={deleted} busy={busy} onRestore={restore} />}
          </>
        )}

        <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
          <AlertDialogContent className="bg-popover">
            <AlertDialogHeader>
              <AlertDialogTitle>Merge these chapters?</AlertDialogTitle>
              <AlertDialogDescription>
                This keeps <b>"{confirm && titleOf(confirm.keepId)}"</b> and removes {confirm?.removeIds?.length} duplicate
                chapter(s). Removed chapters are backed up and can be restored with one click from the "Recently removed"
                list — nothing is permanently lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="dedupe-merge-confirm"
                onClick={() => doMerge(confirm.group, confirm.keepId, confirm.removeIds)}>
                Merge &amp; remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

function RestorePanel({ deleted, busy, onRestore }) {
  return (
    <div className="mt-2 rounded-lg border border-border bg-background/40 p-3 text-left" data-testid="dedupe-restore-panel">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-2">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Recently removed (recoverable)
      </div>
      <div className="space-y-1.5">
        {deleted.map((b) => (
          <div key={b.id} className="flex items-center gap-2 text-xs" data-testid={`dedupe-deleted-${b.id}`}>
            <span className="flex-1 min-w-0 truncate">{b.title} <span className="text-muted-foreground">· {b.word_count} words</span></span>
            <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs shrink-0"
              data-testid={`dedupe-restore-${b.id}`} disabled={!!busy} onClick={() => onRestore(b)}>
              {busy === `restore:${b.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Restore
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
